import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Upload, ImagePlus, Link2, Check, Copy } from 'lucide-react';
import api from '@/lib/api';
import { postcardShareText } from '@/lib/giraffePaths';
import { copyPostcardImage } from '@/lib/postcardShare';

/**
 * CompositionTool — postcard composition editor (compose a photo + text into a
 * shareable postcard). Fragments are retired; postcards are the trip record.
 *
 * Props:
 *   photo?: { url, id } — pre-loaded photo (skips the picker if provided)
 *   mode: 'postcard' — retained for callers; only postcard behaviour remains
 *   onClose: () => void
 *   onSave: (data) => void
 *   initialData?: { title, body, field3, story, gallery, trip_id } — pre-fills for editing
 *   editingId?: string — when set, Save calls PUT instead of POST
 */

const CANVAS_W = 1600;
const CANVAS_H = 2000;   // default (portrait) height used until a photo is loaded
const MIN_H = 900;       // widest postcard (~16:9) before a very wide photo is cropped
const MAX_H = 2000;      // tallest postcard — keeps portrait photos behaving as before
const ACCENT = { postcard: '#F5A623', fragment: '#10B981' };

export default function CompositionTool({ photo: initialPhoto, mode = 'postcard', tripId: propTripId, onClose, onSave, initialData, editingId }) {
  const [photo, setPhoto] = useState(initialPhoto || null);
  const [photoImg, setPhotoImg] = useState(null);
  const [title, setTitle] = useState(initialData?.title || '');
  const [body, setBody] = useState(initialData?.body || '');
  const [field3, setField3] = useState(initialData?.field3 || '');
  // Long-form narrative shown under the postcard on its share page. Not drawn
  // on the image, so it does not affect the canvas.
  const [story, setStory] = useState(initialData?.story || '');
  // Extra photos shown in a slider under the postcard. Uploaded to Cloudinary
  // (persisted URLs), unlike the main photo which is composited into the image.
  const [gallery, setGallery] = useState(initialData?.gallery || []);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const [galleryPickerTab, setGalleryPickerTab] = useState('upload');
  const galleryRef = useRef(null);
  const [saved, setSaved] = useState(false);
  const [savedData, setSavedData] = useState(null);   // saved postcard (for the share link)
  const [linkCopied, setLinkCopied] = useState(false);
  const [imageCopied, setImageCopied] = useState(false);
  const [pickerTab, setPickerTab] = useState('upload');
  const [tripPhotos, setTripPhotos] = useState([]);
  const [framedPhotos, setFramedPhotos] = useState([]);
  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(propTripId || '');
  // Trip this postcard is linked to. undefined = not chosen yet (default to the
  // active trip once trips load); '' = deliberately no trip; else a trip id.
  const [linkedTripId, setLinkedTripId] = useState(
    initialData?.trip_id === undefined ? undefined : (initialData.trip_id || '')
  );
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const tripId = propTripId || selectedTripId;

  const accent = ACCENT[mode] || '#6B35B8';

  // Load the trip list for the postcard trip selector; default a new postcard's
  // link to the active trip (editing keeps its stored link, incl. "no trip").
  useEffect(() => {
    if (mode !== 'postcard') return;
    api.get('/trips').then(r => {
      const data = r.data || [];
      setTrips(data);
      setLinkedTripId(prev => {
        if (prev !== undefined) return prev;
        const active = data.find(t => t.status !== 'archived' && (t.effective_status || t.status) === 'active');
        return active ? active.id : '';
      });
    }).catch(() => {});
  }, [mode]);

  // Load photo image when photo URL changes
  useEffect(() => {
    if (!photo?.url) { setPhotoImg(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setPhotoImg(img);
    img.onerror = () => setPhotoImg(null);
    img.src = photo.url;
  }, [photo?.url]);

  // Canvas keeps a fixed width and takes its HEIGHT from the photo's aspect
  // ratio (clamped), so a landscape photo yields a landscape postcard instead
  // of being cover-cropped into a portrait frame. Width is constant, so all
  // text sizing/wrapping below is unaffected.
  const canvasH = photoImg?.naturalWidth
    ? Math.max(MIN_H, Math.min(MAX_H, Math.round(CANVAS_W * photoImg.naturalHeight / photoImg.naturalWidth)))
    : CANVAS_H;

  // Redraw canvas whenever inputs change
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_W, canvasH);

    // Photo background — cover fill (exact fit now that the canvas matches the
    // photo's aspect, so nothing is cropped except very extreme panoramas).
    if (photoImg) {
      const iw = photoImg.naturalWidth, ih = photoImg.naturalHeight;
      const scale = Math.max(CANVAS_W / iw, canvasH / ih);
      const sw = iw * scale, sh = ih * scale;
      const sx = (CANVAS_W - sw) / 2, sy = (canvasH - sh) / 2;
      ctx.drawImage(photoImg, sx, sy, sw, sh);
    } else {
      ctx.fillStyle = '#0a0a0f';
      ctx.fillRect(0, 0, CANVAS_W, canvasH);
    }

    // Only draw the dark caption bar when there is text to sit on it.
    const hasText = !!(title.trim() || body.trim() || field3.trim());
    if (!hasText) return;

    // Measure text block height dynamically
    const pad = 48;
    const maxW = CANVAS_W - pad * 2;
    let contentH = pad; // top padding

    ctx.textBaseline = 'top';
    if (title) {
      ctx.font = 'italic 56px Georgia, serif';
      contentH += wrapText(ctx, title, maxW).length * 68 + 24;
    }
    if (body) {
      ctx.font = 'italic 32px Georgia, serif';
      contentH += wrapText(ctx, body, maxW).length * 48 + 32;
    }
    if (field3) {
      contentH += 32; // mono line + spacing
    }
    contentH += pad; // bottom padding

    const blockH = Math.max(160, contentH);
    const blockY = canvasH - blockH;

    ctx.fillStyle = 'rgba(10, 10, 15, 0.85)';
    ctx.fillRect(0, blockY, CANVAS_W, blockH);

    let textY = blockY + pad;

    // Title — serif italic 56px
    if (title) {
      ctx.font = 'italic 56px Georgia, serif';
      ctx.fillStyle = '#f0f0f5';
      const lines = wrapText(ctx, title, maxW);
      for (const line of lines) {
        ctx.fillText(line, pad, textY);
        textY += 68;
      }
      textY += 24;
    }

    // Body — serif italic 32px
    if (body) {
      ctx.font = 'italic 32px Georgia, serif';
      ctx.fillStyle = '#b0b4c0';
      const lines = wrapText(ctx, body, maxW);
      for (const line of lines) {
        ctx.fillText(line, pad, textY);
        textY += 48;
      }
      textY += 32;
    }

    // Third field — mono 24px
    if (field3) {
      ctx.font = '24px "JetBrains Mono", monospace';
      ctx.fillStyle = '#6b7084';
      ctx.fillText(field3, pad, textY);
    }
  }, [photoImg, title, body, field3, canvasH]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  // Text wrapping helper
  function wrapText(ctx, text, maxW) {
    const words = text.split(' ');
    const lines = [];
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxW && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines.length ? lines : [''];
  }

  const getDataUrl = () => {
    const canvas = canvasRef.current;
    // JPEG (not PNG) — the composited postcard is a photo, so this cuts the
    // uploaded payload from several MB to a few hundred KB, which matters on
    // the backend's slow inbound link. The canvas is fully opaque, so there's
    // no transparency to lose.
    return canvas ? canvas.toDataURL('image/jpeg', 0.9) : '';
  };

  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [error, setError] = useState(null);

  // Persist the chosen main photo to Cloudinary so source_photo_url survives the
  // session. Previously we stored the local blob: URL, which died on reload and
  // left the canvas blank when re-editing. Shows the local blob for instant
  // preview, then swaps in the persistent URL once the upload returns.
  const uploadMainPhoto = async (file) => {
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setPhoto({ url: localUrl, id: 'upload' });
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data?.url) setPhoto({ url: data.url, id: 'upload' });
    } catch (err) {
      console.error('Main photo upload failed:', err);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSave = async () => {
    if (!photo?.url || saving || photoUploading) return;
    setSaving(true);
    setError(null);
    try {
      const imageData = getDataUrl();
      const payload = {
        source_photo_url: photo.url,
        title: title.trim(),
        body: body.trim(),
        field3: field3.trim(),
        image_data: imageData,
      };
      payload.story = story.trim();
      payload.gallery = gallery;
      // Only send trip_id once chosen; omitting it lets the backend default to
      // the active trip. '' means the user picked "No trip".
      if (linkedTripId !== undefined) payload.trip_id = linkedTripId;
      let data;
      const endpoint = editingId ? `/giraffe/postcards/${editingId}` : '/giraffe/postcards';
      const method = editingId ? 'put' : 'post';
      ({ data } = await api[method](endpoint, payload));
      if (onSave) onSave(data);
      setSavedData(data);      // the saved postcard, incl. public_slug — for the share link
      setSaved(true);          // persistent confirmation — dismissed by the user, not a timer
    } catch (err) {
      console.error('CompositionTool save failed:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err?.message || 'Save failed. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const dataUrl = getDataUrl();
    const link = document.createElement('a');
    link.download = `composition_${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    uploadMainPhoto(file);
  };

  const uploadGalleryFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) setGallery(prev => [...prev, data.url]);
      }
    } catch (err) {
      console.error('Gallery upload failed:', err);
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGallerySelect = (e) => {
    const files = e.target.files;
    if (galleryRef.current) galleryRef.current.value = '';
    uploadGalleryFiles(files);
  };

  // Add/remove an existing (already-hosted) URL to the extra-photos gallery —
  // used when picking from Framed, mirroring the main photo picker's options.
  const toggleGalleryUrl = (url) => {
    if (!url) return;
    setGallery(prev => prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]);
  };

  const removeGalleryPhoto = (url) => setGallery(prev => prev.filter(u => u !== url));

  // Fetch trip photos for picker
  useEffect(() => {
    if (pickerTab === 'trip' && tripId && tripPhotos.length === 0) {
      api.get(`/trips/${tripId}`).then(r => {
        const entries = r.data?.entries || [];
        const photos = entries.filter(e => e.photo_url).map(e => ({ url: e.photo_url, id: e.id }));
        setTripPhotos(photos);
      }).catch(() => console.warn('CompositionTool: could not fetch trip photos'));
    }
  }, [pickerTab, tripId]);

  // Fetch Framed photos — for either the main photo picker or the extra-photos picker
  useEffect(() => {
    const framedWanted = pickerTab === 'framed' || (galleryPickerOpen && galleryPickerTab === 'framed');
    if (framedWanted && framedPhotos.length === 0) {
      api.get('/photos').then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        setFramedPhotos(data.slice(0, 30).map(p => ({ url: p.url, id: p.id })));
      }).catch(() => console.warn('CompositionTool: could not fetch Framed photos'));
    }
  }, [pickerTab, galleryPickerOpen, galleryPickerTab]);

  const showPicker = !photo;

  const pickerTabs = [{ id: 'upload', label: 'Upload' }, { id: 'framed', label: 'From Framed' }];

  const galleryPickerTabs = [{ id: 'upload', label: 'Upload' }, { id: 'framed', label: 'From Framed' }];

  return (
    <>
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          maxWidth: 720, width: '100%', maxHeight: '95vh',
          overflowY: 'auto', padding: 32,
          background: '#0a0a0f', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.06)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <span style={{
            fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: '1.5px', color: '#6b7084',
          }}>
            COMPOSE POSTCARD
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7084', padding: 4 }}>
            <X size={24} />
          </button>
        </div>

        {/* Photo Picker (Giraffe fragment, no photo) */}
        {showPicker && (
          <div>
            {/* Picker tabs */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {pickerTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setPickerTab(tab.id)}
                  style={{
                    fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500,
                    textTransform: 'uppercase', letterSpacing: '1px',
                    color: pickerTab === tab.id ? accent : '#6b7084',
                    borderBottom: pickerTab === tab.id ? `2px solid ${accent}` : '2px solid transparent',
                    background: 'none', border: 'none', borderBottomStyle: 'solid',
                    cursor: 'pointer', padding: '8px 4px',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Upload tab */}
            {pickerTab === 'upload' && (
              <div
                style={{
                  border: '2px dashed rgba(255,255,255,0.08)', borderRadius: 12,
                  padding: 48, textAlign: 'center', cursor: 'pointer',
                }}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); uploadMainPhoto(e.dataTransfer.files?.[0]); }}
              >
                <Upload size={32} style={{ color: '#6b7084', marginBottom: 12, display: 'inline-block' }} />
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#6b7084' }}>
                  Drop an image or click to upload
                </p>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
              </div>
            )}

            {/* Trip photos tab */}
            {pickerTab === 'trip' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {tripPhotos.length === 0 && (
                  <p style={{ gridColumn: '1/-1', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b7084', textAlign: 'center', padding: 32 }}>
                    No photos in this trip
                  </p>
                )}
                {tripPhotos.map(p => (
                  <img
                    key={p.id}
                    src={p.url}
                    alt=""
                    style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.06)' }}
                    onClick={() => setPhoto(p)}
                  />
                ))}
              </div>
            )}

            {/* Framed photos tab */}
            {pickerTab === 'framed' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {framedPhotos.length === 0 && (
                  <p style={{ gridColumn: '1/-1', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b7084', textAlign: 'center', padding: 32 }}>
                    No photos available
                  </p>
                )}
                {framedPhotos.map(p => (
                  <img
                    key={p.id}
                    src={p.url}
                    alt=""
                    style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.06)' }}
                    onClick={() => setPhoto(p)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Editor (photo selected) */}
        {photo && (
          <>
            {/* Canvas preview */}
            <div style={{ marginBottom: 12 }}>
              <canvas
                ref={canvasRef}
                width={CANVAS_W}
                height={canvasH}
                style={{
                  width: '100%', height: 'auto',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              />
            </div>

            {/* Change the main photo — same uploader as the initial picker */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              {photoUploading && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#6b7084' }}>Uploading photo…</span>
              )}
              <button
                type="button"
                onClick={() => setPhoto(null)}
                disabled={photoUploading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '8px 14px', color: '#b0b4c0',
                  fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500,
                  cursor: photoUploading ? 'default' : 'pointer', opacity: photoUploading ? 0.6 : 1,
                }}
              >
                <ImagePlus size={15} /> Change photo
              </button>
            </div>

            {/* Trip link — which trip this postcard belongs to (defaults to active) */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#6b7084', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Trip</label>
              <select
                value={linkedTripId ?? ''}
                onChange={e => setLinkedTripId(e.target.value)}
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '10px 14px', color: '#f0f0f5',
                  fontFamily: "'Inter', sans-serif", fontSize: 14, outline: 'none',
                }}
              >
                <option value="">No trip</option>
                {trips.filter(t => t.status !== 'archived').map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>

            {/* Text inputs */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Title"
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '12px 16px', color: '#f0f0f5',
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 16,
                  outline: 'none',
                }}
              />
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Body text"
                rows={3}
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '12px 16px', color: '#b0b4c0',
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14,
                  outline: 'none', resize: 'vertical',
                }}
              />
              <input
                type="text"
                value={field3}
                onChange={e => setField3(e.target.value)}
                placeholder="Date, location, attribution…"
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '12px 16px', color: '#6b7084',
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                  outline: 'none',
                }}
              />
              {mode === 'postcard' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b7084' }}>
                    Story — shown under the postcard, not on the image
                  </label>
                  <textarea
                    value={story}
                    onChange={e => setStory(e.target.value)}
                    placeholder="Tell the story behind this postcard…"
                    rows={5}
                    style={{
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8, padding: '12px 16px', color: '#b0b4c0',
                      fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.6,
                      outline: 'none', resize: 'vertical',
                    }}
                  />

                  {/* Extra photos — shown in a slider under the postcard */}
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b7084', marginTop: 8 }}>
                    More photos — swipeable gallery under the postcard
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {gallery.map((url) => (
                      <div key={url} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button
                          type="button"
                          onClick={() => removeGalleryPhoto(url)}
                          style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(10,10,15,0.8)', color: '#f0f0f5', fontSize: 12, lineHeight: '18px', cursor: 'pointer', padding: 0 }}
                          aria-label="Remove photo"
                        >×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => { setGalleryPickerTab('upload'); setGalleryPickerOpen(true); }}
                      disabled={galleryUploading}
                      style={{ width: 64, height: 64, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.02)', color: '#6b7084', fontSize: galleryUploading ? 10 : 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {galleryUploading ? '…' : '+'}
                    </button>
                    <input ref={galleryRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleGallerySelect} />
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleDownload}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  color: '#b0b4c0', fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Download
              </button>
              <button
                onClick={handleSave}
                disabled={saving || photoUploading}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: 8,
                  background: accent, border: 'none',
                  color: '#ffffff', fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600,
                  cursor: (saving || photoUploading) ? 'default' : 'pointer',
                  opacity: (saving || photoUploading) ? 0.7 : 1,
                }}
              >
                {saving ? 'Composing...' : photoUploading ? 'Uploading photo…' : 'Save'}
              </button>
            </div>

            {/* Error — persistent until dismissed, so a failed save is never silent */}
            {error && (
              <div style={{
                marginTop: 12, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                padding: '10px 14px', borderRadius: 8,
                background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
              }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#fca5a5', lineHeight: 1.4 }}>
                  {error}
                </span>
                <button onClick={() => setError(null)} aria-label="Dismiss"
                  style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>
                  ×
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>

    {/* Saved — persistent confirmation, dismissed by the user (Done or click-away) */}
    {saved && (
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 130,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: '#0a0a0f', border: '1px solid rgba(16,185,129,0.35)',
            borderRadius: 14, padding: '32px 40px', textAlign: 'center', maxWidth: 360,
          }}
        >
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span style={{ color: '#10B981', fontSize: 30, lineHeight: 1 }}>✓</span>
          </div>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#f0f0f5', margin: '0 0 6px' }}>Saved</p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#b0b4c0', margin: '0 0 22px' }}>Your postcard is saved and ready to share.</p>
          {mode === 'postcard' && savedData && savedData.composed_image_url && (
            <button
              onClick={async () => {
                try {
                  await copyPostcardImage(savedData);
                  setImageCopied(true);
                  setTimeout(() => setImageCopied(false), 2000);
                } catch { /* clipboard unavailable */ }
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#f0f0f5', fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 500, cursor: 'pointer', marginBottom: 12, width: '100%', justifyContent: 'center' }}
            >
              {imageCopied
                ? <><Check size={15} /> Image copied</>
                : <><Copy size={15} /> Copy image</>}
            </button>
          )}
          {mode === 'postcard' && savedData && (
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(postcardShareText(savedData));
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                } catch { /* clipboard unavailable */ }
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#f0f0f5', fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 500, cursor: 'pointer', marginBottom: 12, width: '100%', justifyContent: 'center' }}
            >
              {linkCopied
                ? <><Check size={15} /> Copied to share</>
                : <><Link2 size={15} /> Copy share link</>}
            </button>
          )}
          <button
            onClick={onClose}
            style={{ padding: '10px 28px', borderRadius: 999, background: '#10B981', border: 'none', color: '#fff', fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' }}
          >
            Done
          </button>
        </div>
      </div>
    )}

    {/* Extra-photos picker — same Upload / From Framed options as the main photo */}
    {galleryPickerOpen && (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 120,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        onClick={() => setGalleryPickerOpen(false)}
      >
        <div
          style={{
            maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: 28,
            background: '#0a0a0f', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#6b7084' }}>
              Add photos
            </span>
            <button onClick={() => setGalleryPickerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7084', padding: 4 }}>
              <X size={22} />
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {galleryPickerTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setGalleryPickerTab(tab.id)}
                style={{
                  fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500,
                  textTransform: 'uppercase', letterSpacing: '1px',
                  color: galleryPickerTab === tab.id ? accent : '#6b7084',
                  borderBottom: galleryPickerTab === tab.id ? `2px solid ${accent}` : '2px solid transparent',
                  background: 'none', border: 'none', borderBottomStyle: 'solid',
                  cursor: 'pointer', padding: '8px 4px',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Upload tab */}
          {galleryPickerTab === 'upload' && (
            <div
              style={{ border: '2px dashed rgba(255,255,255,0.08)', borderRadius: 12, padding: 48, textAlign: 'center', cursor: 'pointer' }}
              onClick={() => galleryRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); uploadGalleryFiles(e.dataTransfer.files); }}
            >
              <Upload size={32} style={{ color: '#6b7084', marginBottom: 12, display: 'inline-block' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#6b7084' }}>
                {galleryUploading ? 'Uploading…' : 'Drop images or click to upload'}
              </p>
            </div>
          )}

          {/* From Framed tab */}
          {galleryPickerTab === 'framed' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {framedPhotos.length === 0 && (
                <p style={{ gridColumn: '1/-1', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b7084', textAlign: 'center', padding: 32 }}>
                  No photos available
                </p>
              )}
              {framedPhotos.map(p => {
                const selected = gallery.includes(p.url);
                return (
                  <div key={p.id} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => toggleGalleryUrl(p.url)}>
                    <img
                      src={p.url}
                      alt=""
                      style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: selected ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.06)' }}
                    />
                    {selected && (
                      <div style={{ position: 'absolute', top: 6, right: 6, width: 20, height: 20, borderRadius: '50%', background: accent, color: '#fff', fontSize: 13, lineHeight: '20px', textAlign: 'center' }}>✓</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Done */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button
              onClick={() => setGalleryPickerOpen(false)}
              style={{ padding: '9px 20px', borderRadius: 8, background: accent, border: 'none', color: '#fff', fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Done{gallery.length ? ` (${gallery.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
